/**
 * Where you can be in this application.
 *
 * A hash router, not the History API. That is a deliberate and slightly unusual choice, and
 * the reason is the deployment: the API serves the bundle and falls back to `index.html` for
 * unknown paths, which works — but the moment this is put behind a CDN, a static host, or a
 * path-prefixed reverse proxy, that fallback has to be re-established in each one. A hash
 * route never leaves the browser, so `/#/packages/123` is correct everywhere with no server
 * configuration at all.
 *
 * The cost is a `#` in the address bar. For an internal governance tool that is a fair trade
 * against a class of deployment bug that only shows up after someone has already shared a
 * link that 404s.
 *
 * Routes are parsed rather than pattern-matched by a library. There are six of them; a router
 * dependency would be more configuration than code.
 */

export type Route =
  | { name: 'dashboard' }
  | { name: 'projects' }
  | { name: 'project'; projectId: string }
  | { name: 'package'; packageId: string; tab: string }
  | { name: 'inbox' }
  | { name: 'application'; applicationId: string }
  | { name: 'periods' }
  | { name: 'builder'; slug: string }
  | { name: 'register'; slug: string }
  | { name: 'history'; slug: string; rowId: string }
  | { name: 'ingestion' }
  | { name: 'create' }

export const DEFAULT_ROUTE: Route = { name: 'dashboard' }

/** The rail section a route belongs to, so the active marker is right on every screen. */
export function sectionOf(route: Route): string {
  switch (route.name) {
    // A package is reached from its project and has no rail item of its own, so the rail
    // keeps pointing at where you came from rather than going dark three levels deep.
    case 'projects':
    case 'project':
    case 'package':
      return 'projects'
    case 'inbox':
    case 'application':
      return 'inbox'
    case 'periods':
      return 'periods'
    case 'ingestion':
      return 'ingestion'
    // No rail item matches, and that is the answer. New entry is the button above the list,
    // not one of the destinations in it, so nothing lights up — which is better than the
    // default lighting up Dashboard while somebody stands on a different screen.
    case 'create':
      return 'create'
    case 'builder':
      return 'builder'
    // A register and a row's history both belong to the register list in the rail, so the
    // marker stays on the register that was opened rather than jumping to Projects.
    case 'register':
    case 'history':
      return 'registers'
    default:
      return 'dashboard'
  }
}

export function toPath(route: Route): string {
  switch (route.name) {
    case 'dashboard':
      return '#/'
    case 'projects':
      return '#/projects'
    case 'project':
      return `#/projects/${route.projectId}`
    case 'package':
      return `#/packages/${route.packageId}/${route.tab}`
    case 'inbox':
      return '#/inbox'
    case 'application':
      return `#/applications/${route.applicationId}`
    case 'periods':
      return '#/periods'
    case 'ingestion':
      return '#/ingestion'
    case 'builder':
      return `#/builder/${route.slug}`
    case 'register':
      return `#/registers/${route.slug}`
    case 'history':
      return `#/registers/${route.slug}/${route.rowId}/history`
    case 'create':
      return '#/create'
  }
}

export function parse(hash: string): Route {
  const path = hash.replace(/^#/, '').replace(/^\/+/, '')
  const [head, first, second] = path.split('/')

  if (!head) return DEFAULT_ROUTE
  if (head === 'projects') {
    return first ? { name: 'project', projectId: first } : { name: 'projects' }
  }
  if (head === 'packages' && first) {
    // The tab is part of the address on purpose: a coordinator sending "look at the risks on
    // this package" should be able to paste a link that opens on the risks.
    return { name: 'package', packageId: first, tab: second || 'summary' }
  }
  if (head === 'inbox') return { name: 'inbox' }
  if (head === 'applications' && first) return { name: 'application', applicationId: first }
  if (head === 'periods') return { name: 'periods' }
  if (head === 'ingestion') return { name: 'ingestion' }
  // The register is part of the address: "the risk register's fields" is a link.
  if (head === 'builder') return { name: 'builder', slug: first || 'risks' }
  // `#/registers/risks/<id>/history`. Addressable on purpose: "here is what happened to
  // this row" is a link somebody pastes into an email, which is most of why the screen
  // exists at all.
  if (head === 'registers' && first && second) {
    return { name: 'history', slug: first, rowId: second }
  }
  // `#/registers/risks` — the register across the whole portfolio. The eight rail entries
  // used to point at `#/projects`, all of them, which is why none of them appeared to work.
  if (head === 'registers' && first) return { name: 'register', slug: first }
  if (head === 'create') return { name: 'create' }

  return DEFAULT_ROUTE
}

export function navigate(route: Route): void {
  window.location.hash = toPath(route).replace(/^#/, '')
}
